import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilePlus2, LayoutGrid, Search, Sparkles, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { canAccessRoute, getRouteAccessRule } from "Routes/routeAccess";
import { getAuthPayload, hasPermission, isContractorUser } from "Common/permissions";

type SearchGroupKey = "pages" | "actions";

type SearchResult = {
  id: string;
  title: string;
  subtitle: string;
  url: string;
  keywords: string;
  roles: string[];
  group: SearchGroupKey;
};

type SearchGroups = Record<SearchGroupKey, SearchResult[]>;

const GROUPS: Array<{ key: SearchGroupKey; label: string; icon: LucideIcon }> = [
  { key: "pages", label: "Your workspace", icon: LayoutGrid },
  { key: "actions", label: "Quick actions", icon: Sparkles },
];

const ALL_ACCOUNT_ROLES = [
  "admin",
  "contractor",
  "homeowner",
  "real_estate_agent",
  "broker",
  "subcontractor",
  "inspector",
];

const SEARCH_INDEX: SearchResult[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    subtitle: "Return to your workspace overview",
    url: "/dashboard",
    keywords: "home overview summary workspace",
    roles: ALL_ACCOUNT_ROLES,
    group: "pages",
  },
  {
    id: "profile",
    title: "Profile",
    subtitle: "View and update your account profile",
    url: "/pages-profile-settings",
    keywords: "account settings personal company details",
    roles: ["contractor", "homeowner", "real_estate_agent", "broker", "subcontractor", "inspector"],
    group: "pages",
  },
  {
    id: "contractor-leads",
    title: "Leads Pro",
    subtitle: "Browse available project opportunities",
    url: "/pages-projects",
    keywords: "lead leads jobs projects opportunities new work",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "requested-leads",
    title: "Requested Leads",
    subtitle: "Review leads you have requested",
    url: "/project-requested",
    keywords: "lead leads requested pending jobs",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "accepted-leads",
    title: "Accepted Leads",
    subtitle: "Open leads currently in progress",
    url: "/project-ongoing-leads",
    keywords: "lead leads accepted ongoing active jobs",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "lead-history",
    title: "Lead History",
    subtitle: "View completed and past lead activity",
    url: "/leadHistory",
    keywords: "lead leads history completed past archived jobs",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "homeowner-projects",
    title: "Projects",
    subtitle: "Track your home projects and leads",
    url: "/pages-projects",
    keywords: "project projects leads jobs renovation",
    roles: ["homeowner"],
    group: "pages",
  },
  {
    id: "contracts",
    title: "Contracts",
    subtitle: "View contracts, signatures, and project documents",
    url: "/get-contracts",
    keywords: "contract contracts agreement agreements signature signed documents",
    roles: ["admin", "contractor", "homeowner", "subcontractor", "inspector"],
    group: "pages",
  },
  {
    id: "team",
    title: "Team",
    subtitle: "Manage company staff and permissions",
    url: "/team_management",
    keywords: "team staff employee employees member members permissions",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "wallet",
    title: "My Wallet",
    subtitle: "Review balances and payment activity",
    url: "/wallet",
    keywords: "wallet money balance billing payments transactions",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "invoices",
    title: "Invoices",
    subtitle: "View and manage contractor invoices",
    url: "/invoices-list",
    keywords: "invoice invoices billing payment bills",
    roles: ["contractor"],
    group: "pages",
  },
  {
    id: "mechanics-liens",
    title: "Mechanics Lien Intakes",
    subtitle: "Review mechanics lien intake requests",
    url: "/mechanics-lien-intakes",
    keywords: "mechanic mechanics lien liens intake legal",
    roles: ["admin", "contractor", "subcontractor"],
    group: "pages",
  },
  {
    id: "chat",
    title: "Chat",
    subtitle: "Open conversations and project messages",
    url: "/apps-chat",
    keywords: "chat chats message messages conversation inbox",
    roles: ["admin", "contractor", "homeowner", "real_estate_agent", "broker"],
    group: "pages",
  },
  {
    id: "logan-ai",
    title: "Logan AI",
    subtitle: "Get help with your RELiBUILD workspace",
    url: "/logan-ai",
    keywords: "logan ai assistant help guide support",
    roles: ALL_ACCOUNT_ROLES,
    group: "pages",
  },
  {
    id: "support",
    title: "Support",
    subtitle: "View or create support tickets",
    url: "/apps-tickets-list",
    keywords: "support help ticket tickets issue problem",
    roles: ["admin", "contractor", "homeowner"],
    group: "pages",
  },
  {
    id: "recent-contractors",
    title: "Recent Contractors",
    subtitle: "Return to contractors you recently viewed",
    url: "/recent-contractors",
    keywords: "contractor contractors recent viewed professionals",
    roles: ["homeowner"],
    group: "pages",
  },
  {
    id: "suggested-contractors",
    title: "Suggested Contractors",
    subtitle: "Explore contractors recommended for you",
    url: "/suggested-contractors",
    keywords: "contractor contractors suggested recommended professionals",
    roles: ["homeowner"],
    group: "pages",
  },
  {
    id: "recommended-properties",
    title: "Recommended Properties",
    subtitle: "View properties selected for you",
    url: "/recommended-properties",
    keywords: "property properties home homes recommended real estate",
    roles: ["homeowner"],
    group: "pages",
  },
  {
    id: "available-property-leads",
    title: "Available Property Leads",
    subtitle: "Browse available real-estate opportunities",
    url: "/agent-property-leads",
    keywords: "property lead leads available opportunity opportunities",
    roles: ["real_estate_agent", "broker"],
    group: "pages",
  },
  {
    id: "my-property-leads",
    title: "My Property Leads",
    subtitle: "Track your property leads",
    url: "/agent-my-property-leads",
    keywords: "property lead leads mine assigned tracking",
    roles: ["real_estate_agent", "broker"],
    group: "pages",
  },
  {
    id: "explore-contractors",
    title: "Explore Contractors",
    subtitle: "Find contractors for your clients",
    url: "/apps-real-estate-explore-contractor",
    keywords: "contractor contractors search discover professional",
    roles: ["real_estate_agent", "broker"],
    group: "pages",
  },
  {
    id: "admin-contractors",
    title: "Contractors",
    subtitle: "Manage contractor accounts and companies",
    url: "/pages-all-contractors",
    keywords: "contractor contractors company companies users manage",
    roles: ["admin"],
    group: "pages",
  },
  {
    id: "admin-homeowners",
    title: "Homeowners",
    subtitle: "Manage homeowner accounts",
    url: "/pages-all-homeowners",
    keywords: "homeowner homeowners customer customers users manage",
    roles: ["admin"],
    group: "pages",
  },
  {
    id: "admin-leads",
    title: "All Leads",
    subtitle: "Review leads across the platform",
    url: "/pages-all-leads",
    keywords: "lead leads jobs projects platform manage",
    roles: ["admin"],
    group: "pages",
  },
  {
    id: "admin-agents",
    title: "Real Estate Agents",
    subtitle: "Manage agent and broker accounts",
    url: "/pages-all-real-estate-agents",
    keywords: "agent agents broker brokers real estate users",
    roles: ["admin"],
    group: "pages",
  },
  {
    id: "platform-access",
    title: "Platform Access",
    subtitle: "Manage platform staff access",
    url: "/platform-access",
    keywords: "admin admins staff access permissions roles security",
    roles: ["admin"],
    group: "pages",
  },
  {
    id: "create-contract",
    title: "Create Contract",
    subtitle: "Start a new contract",
    url: "/create-contract",
    keywords: "new contract agreement create draft",
    roles: ["admin", "contractor"],
    group: "actions",
  },
  {
    id: "refer-and-earn",
    title: "Refer & Earn",
    subtitle: "Open your contractor referral program",
    url: "/wallet?tab=referrals",
    keywords: "refer referral invite earn rewards",
    roles: ["contractor"],
    group: "actions",
  },
  {
    id: "marketing",
    title: "Marketing & Website Development",
    subtitle: "Grow your contractor business",
    url: "/marketing",
    keywords: "marketing website development business growth",
    roles: ["contractor"],
    group: "actions",
  },
  {
    id: "insurance",
    title: "P&C and Workers Comp",
    subtitle: "Explore contractor insurance options",
    url: "/property_insurance",
    keywords: "insurance workers compensation property casualty coverage",
    roles: ["contractor"],
    group: "actions",
  },
  {
    id: "add-property",
    title: "Add Property",
    subtitle: "Add and manage a property",
    url: "/property-manager",
    keywords: "property properties add create manage home",
    roles: ["homeowner"],
    group: "actions",
  },
  {
    id: "add-homeowner",
    title: "Add Homeowner",
    subtitle: "Invite a homeowner to RELiBUILD",
    url: "/apps-real-estate-agent-homeowner",
    keywords: "homeowner invite add client customer",
    roles: ["real_estate_agent", "broker"],
    group: "actions",
  },
];

const normalizeRole = (role: string) => {
  const normalized = String(role || "").trim().toLowerCase();
  if (normalized === "testcontractor") return "contractor";
  if (normalized === "real_esate_agent") return "real_estate_agent";
  return normalized;
};

const getSearchTokens = (value: string) =>
  value.toLowerCase().match(/[a-z0-9]+/g) || [];

const fuzzyTokenMatch = (text: string, token: string) => {
  if (text.includes(token)) return true;
  if (token.length < 4) return false;

  return text.split(/[^a-z0-9]+/).some((word) => {
    let cursor = 0;
    for (const character of token) {
      cursor = word.indexOf(character, cursor);
      if (cursor === -1) return false;
      cursor += 1;
    }
    return true;
  });
};

const canOpenResult = (result: SearchResult) => {
  if (result.id === "create-contract") {
    const auth = getAuthPayload();
    const canCreateContract =
      auth.userType === "admin" ||
      (isContractorUser(auth.userType) && hasPermission("CREATE_CONTRACT"));
    if (!canCreateContract) return false;
  }

  const path = result.url.split("?")[0];
  const rule = getRouteAccessRule(path);
  return !rule || canAccessRoute(rule);
};

const GlobalSmartSearch = ({ userType = "" }: { userType?: string }) => {
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const normalizedRole = normalizeRole(userType);

  const groups = useMemo<SearchGroups>(() => {
    const tokens = getSearchTokens(query.trim());
    const roleResults = SEARCH_INDEX.filter(
      (result) => result.roles.includes(normalizedRole) && canOpenResult(result)
    );
    const matchingResults = tokens.length
      ? roleResults.filter((result) => {
          const searchableText = `${result.title} ${result.subtitle} ${result.keywords}`.toLowerCase();
          return tokens.every((token) => fuzzyTokenMatch(searchableText, token));
        })
      : [];

    return {
      pages: matchingResults.filter((result) => result.group === "pages").slice(0, 6),
      actions: matchingResults.filter((result) => result.group === "actions").slice(0, 4),
    };
  }, [normalizedRole, query]);

  const flatResults = useMemo(() => GROUPS.flatMap(({ key }) => groups[key]), [groups]);

  useEffect(() => {
    const closeResults = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", closeResults);
    return () => document.removeEventListener("mousedown", closeResults);
  }, []);

  const openResult = (result: SearchResult) => {
    setOpen(false);
    setQuery("");
    navigate(result.url);
  };

  const updateQuery = (value: string) => {
    setQuery(value);
    setOpen(value.trim().length >= 2);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!flatResults.length) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index + 1) % flatResults.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => (index <= 0 ? flatResults.length - 1 : index - 1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      openResult(flatResults[activeIndex >= 0 ? activeIndex : 0]);
    }
  };

  let resultIndex = -1;

  return (
    <div ref={rootRef} className="header-smart-search">
      <div className={`header-smart-search__field${open ? " is-open" : ""}`}>
        <Search size={17} aria-hidden />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label="Search your workspace"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls="header-smart-search-results"
          aria-activedescendant={activeIndex >= 0 ? `header-search-result-${activeIndex}` : undefined}
          value={query}
          onChange={(event) => updateQuery(event.target.value)}
          onFocus={() => query.trim().length >= 2 && setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search your workspace"
          autoComplete="off"
        />
        {query ? (
          <button
            type="button"
            className="header-smart-search__clear"
            aria-label="Clear search"
            onClick={() => {
              updateQuery("");
              inputRef.current?.focus();
            }}
          >
            <X size={14} aria-hidden />
          </button>
        ) : null}
      </div>

      {open && query.trim().length >= 2 ? (
        <div id="header-smart-search-results" className="header-smart-search__results" role="listbox">
          {flatResults.length ? (
            GROUPS.map(({ key, label, icon: GroupIcon }) => {
              const results = groups[key];
              if (!results.length) return null;

              return (
                <section className="header-smart-search__group" key={key} aria-label={label}>
                  <div className="header-smart-search__group-label">
                    <GroupIcon size={13} aria-hidden />
                    {label}
                  </div>
                  {results.map((result) => {
                    resultIndex += 1;
                    const currentIndex = resultIndex;
                    const active = activeIndex === currentIndex;

                    return (
                      <button
                        id={`header-search-result-${currentIndex}`}
                        key={result.id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={`header-smart-search__result${active ? " is-active" : ""}`}
                        onMouseEnter={() => setActiveIndex(currentIndex)}
                        onClick={() => openResult(result)}
                      >
                        <span className="header-smart-search__result-icon">
                          {result.group === "actions" ? (
                            <FilePlus2 size={18} aria-hidden />
                          ) : (
                            <GroupIcon size={18} aria-hidden />
                          )}
                        </span>
                        <span className="header-smart-search__result-copy">
                          <strong>{result.title}</strong>
                          <small>{result.subtitle}</small>
                        </span>
                      </button>
                    );
                  })}
                </section>
              );
            })
          ) : (
            <div className="header-smart-search__message">
              <Search size={20} aria-hidden />
              No matching tools found in your workspace
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default GlobalSmartSearch;
